// "Update code" / "Revert code" — install the dashboard's current bot code on
// one or more bots, or put back the code the last update replaced.
// Used by Containers → LXCs and Monitoring → client → Bots.
//   <UpdateCodeModal mode="update"|"revert" bots={[{ id, sip_user, version }]} currentVersion="v0.28" onClose onDone />
// Backend: POST /machines/update-code | /machines/revert-code (each bot is
// handled in a gap between calls) + GET /reload-jobs/:id for progress.
import { useState, useEffect } from 'react'
import { X, Loader2, CheckCircle2, AlertCircle, Clock, Download, Undo2 } from 'lucide-react'
import { audiosocketBotsApi } from '../services/api'

const ROW = 'tw-flex tw-items-center tw-gap-2 tw-border-0 tw-border-b tw-border-solid tw-border-border tw-py-1.5 tw-text-sm last:tw-border-b-0'

export function isOutdated(bot, currentVersion) {
    return !!currentVersion && bot.version !== currentVersion
}

export default function UpdateCodeModal({ mode = 'update', bots, currentVersion, onClose, onDone }) {
    const revert = mode === 'revert'
    const [force, setForce] = useState(false)
    const [job, setJob] = useState(null)
    const [starting, setStarting] = useState(false)
    const [err, setErr] = useState(null)
    const running = job && job.status === 'running'
    const outdated = bots.filter(b => isOutdated(b, currentVersion)).length
    const who = bots.length === 1 ? 'this bot' : `these ${bots.length} bots`

    async function start() {
        setErr(null); setStarting(true)
        try {
            const ids = bots.map(b => b.id)
            const r = revert
                ? await audiosocketBotsApi.revertBotCode(ids)
                : await audiosocketBotsApi.updateBotCode(ids, { force })
            setJob({ id: r.data.job_id, status: 'running', total: r.data.total, done: 0, ok_count: 0, results: [] })
        } catch (e) { setErr(e.response?.data?.error || e.message) }
        finally { setStarting(false) }
    }

    useEffect(() => {
        if (!job?.id || job.status !== 'running') return
        const t = setInterval(async () => {
            try {
                const r = await audiosocketBotsApi.getReloadJob(job.id)
                setJob(r.data.job)
                if (r.data.job.status !== 'running') onDone && onDone(r.data.job)
            } catch (e) { setErr(e.response?.data?.error || e.message) }
        }, 3000)
        return () => clearInterval(t)
    }, [job?.id, job?.status])

    const close = () => { if (!running) onClose() }
    const Icon = revert ? Undo2 : Download

    return (
        <div className="modal-overlay" onClick={close}>
            <div className="modal" style={{ maxWidth: 640 }} onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{revert ? 'Revert' : 'Update'} bot code{bots.length === 1 ? ` — ${bots[0].sip_user}` : ` — ${bots.length} bots`}</h3>
                    <button className="modal-close" onClick={close} disabled={running}><X size={18} /></button>
                </div>
                <div className="modal-body tw-flex tw-flex-col tw-gap-3">
                    {!job ? (revert ? (<>
                        <div className="tw-text-sm">
                            Puts back the code that the last <strong>Update</strong> replaced on {who}.
                        </div>
                        <ul className="tw-m-0 tw-flex tw-flex-col tw-gap-1 tw-pl-5 tw-text-sm tw-text-muted">
                            <li>Restores the newest backup inside each bot (<code>/root/bot-code-backup-*.tgz</code>). Pressing Revert again steps back one more update.</li>
                            <li>Done in a <strong>gap between calls</strong> — no call is cut off. Bots with no backup are left alone.</li>
                            <li>The STT memory guard stays installed — it's harmless with old code and prevents the freezes.</li>
                        </ul>
                    </>) : (<>
                        <div className="tw-text-sm">
                            Installs the dashboard's current bot code <strong>{currentVersion || ''}</strong> (with the STT memory guard)
                            on {who}
                            {bots.length > 1 && <> — <strong>{outdated}</strong> of them are on another version</>}.
                        </div>
                        <ul className="tw-m-0 tw-flex tw-flex-col tw-gap-1 tw-pl-5 tw-text-sm tw-text-muted">
                            <li>Each bot is updated in a <strong>gap between calls</strong> — no call is cut off. A bot that never goes quiet for 3 minutes is skipped (try again later).</li>
                            <li>The old code is backed up inside the bot (<code>/root/bot-code-backup-*.tgz</code>) — <strong>Revert</strong> puts it back.</li>
                            <li>3 bots at a time per server; servers run in parallel. Bots already on {currentVersion || 'the current version'} are left alone.</li>
                        </ul>
                        <label className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-text-sm">
                            <input type="checkbox" checked={force} onChange={e => setForce(e.target.checked)} />
                            Reinstall even on bots that already have {currentVersion || 'the current version'}
                        </label>
                    </>)) : (<>
                        <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold">
                            {running ? <Loader2 size={15} className="spinning tw-text-blue-400" /> : <CheckCircle2 size={15} className="tw-text-green-400" />}
                            {running ? `${revert ? 'Reverting' : 'Updating'}… ${job.done}/${job.total}` : `Done — ${job.ok_count}/${job.total} ok`}
                        </div>
                        <div className="tw-max-h-80 tw-overflow-auto tw-rounded-md tw-border tw-border-solid tw-border-border tw-px-3">
                            {(job.results || []).map(r => (
                                <div key={r.machine_id} className={ROW}>
                                    {r.ok === true ? <CheckCircle2 size={14} className="tw-shrink-0 tw-text-green-400" />
                                        : r.ok === false ? <AlertCircle size={14} className="tw-shrink-0 tw-text-red-400" />
                                        : r.status === 'queued' ? <Clock size={14} className="tw-shrink-0 tw-text-muted" />
                                        : <Loader2 size={14} className="spinning tw-shrink-0 tw-text-blue-400" />}
                                    <strong className="tw-w-14 tw-shrink-0 tw-font-mono">{r.sip_user}</strong>
                                    <span className="tw-w-28 tw-shrink-0 tw-truncate tw-text-xs tw-text-muted">{r.server_name} · {r.lxc_id}</span>
                                    <span className={r.ok === false ? 'tw-text-red-300' : 'tw-text-fg-secondary'}>{r.status}</span>
                                </div>
                            ))}
                        </div>
                        {running && <div className="tw-text-xs tw-text-muted">You can't close this while it runs. Each bot takes ~10–30 s, longer while it waits for a call gap.</div>}
                    </>)}
                    {err && <div className="tw-rounded-md tw-bg-red-500/10 tw-px-3 tw-py-2 tw-text-sm tw-text-red-300">{err}</div>}
                </div>
                <div className="modal-footer tw-flex tw-justify-end tw-gap-2">
                    {!job ? (<>
                        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                        <button className={`btn ${revert ? 'btn-danger' : 'btn-primary'}`} onClick={start} disabled={starting || !bots.length}>
                            {starting ? <Loader2 size={14} className="spinning" /> : <Icon size={14} />} {revert ? 'Revert' : 'Update'} {bots.length === 1 ? 'bot' : `${bots.length} bots`}
                        </button>
                    </>) : (
                        <button className="btn btn-secondary" onClick={close} disabled={running}>Close</button>
                    )}
                </div>
            </div>
        </div>
    )
}
